import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationCenter = ({ notifications, onMarkAsRead, onMarkAllAsRead }) => {
  const [filter, setFilter] = useState('all');

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment':
        return 'Calendar';
      case 'prescription':
        return 'Pill';
      case 'lab':
        return 'TestTube';
      case 'safety':
        return 'AlertTriangle';
      case 'message':
        return 'MessageSquare';
      case 'system':
        return 'Settings';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-success';
      default:
        return 'text-primary';
    }
  };

  const getNotificationBgColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-error/10';
      case 'medium':
        return 'bg-warning/10';
      case 'low':
        return 'bg-success/10';
      default:
        return 'bg-primary/10';
    }
  };

  const filteredNotifications = notifications?.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification?.read;
    return notification?.type === filter;
  });

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  const filterOptions = [
    { value: 'all', label: 'All', count: notifications?.length },
    { value: 'unread', label: 'Unread', count: unreadCount },
    { value: 'appointment', label: 'Appointments', count: notifications?.filter(n => n?.type === 'appointment')?.length },
    { value: 'safety', label: 'Safety', count: notifications?.filter(n => n?.type === 'safety')?.length }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h2 className="text-xl font-semibold text-foreground">Notifications</h2>
          {unreadCount > 0 && (
            <span className="px-2 py-1 bg-error text-error-foreground text-xs font-medium rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="CheckCheck"
            onClick={onMarkAllAsRead}
            disabled={unreadCount === 0}
          >
            Mark All Read
          </Button>
          <Button variant="ghost" size="sm" iconName="Settings" />
        </div>
      </div>
      {/* Filter tabs */}
      <div className="flex items-center space-x-1 mb-6 bg-muted rounded-lg p-1">
        {filterOptions?.map((option) => (
          <button
            key={option?.value}
            onClick={() => setFilter(option?.value)}
            className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-clinical ${
              filter === option?.value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span>{option?.label}</span>
            {option?.count > 0 && (
              <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                filter === option?.value ? 'bg-primary text-primary-foreground' : 'bg-muted-foreground/20'
              }`}>
                {option?.count}
              </span>
            )}
          </button>
        ))}
      </div>
      {/* Notifications list */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {filteredNotifications?.length > 0 ? (
          filteredNotifications?.map((notification) => (
            <div
              key={notification?.id}
              className={`flex items-start space-x-4 p-4 rounded-lg border transition-clinical hover:bg-muted/30 ${
                notification?.read ? 'border-border bg-background' : 'border-primary/20 bg-primary/5'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg ${getNotificationBgColor(notification?.priority)} flex items-center justify-center flex-shrink-0`}>
                <Icon
                  name={getNotificationIcon(notification?.type)}
                  size={20}
                  className={getNotificationColor(notification?.priority)}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-1">
                  <h3 className={`font-medium ${notification?.read ? 'text-muted-foreground' : 'text-foreground'}`}>
                    {notification?.title}
                  </h3>
                  <div className="flex items-center space-x-2 ml-4">
                    <span className="text-xs text-muted-foreground">{notification?.time}</span>
                    {!notification?.read && (
                      <button
                        onClick={() => onMarkAsRead(notification?.id)}
                        className="text-primary hover:text-primary/80 transition-clinical"
                      >
                        <Icon name="Check" size={14} />
                      </button>
                    )}
                  </div>
                </div>
                
                <p className={`text-sm ${notification?.read ? 'text-muted-foreground' : 'text-foreground'}`}>
                  {notification?.message}
                </p>

                {notification?.actionRequired && (
                  <div className="mt-3 flex items-center space-x-2">
                    <Button variant="outline" size="xs" iconName="ExternalLink">
                      Take Action
                    </Button>
                    <Button variant="ghost" size="xs">
                      Dismiss
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No notifications</h3>
            <p className="text-muted-foreground">
              {filter === 'unread' ? 'All caught up! No unread notifications.' : 'You\'re all set for now.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;